import { useEffect, useMemo, useState } from 'react';
import type { OutcomeSurfaceProps } from './surfaceBinding';
import './EngagementCharterSurface.css';

const SLOT_LABELS = {
  purpose: 'What does the client want to achieve?',
  whyNow: 'Why does this need to happen now?',
  success: 'How will the client judge success?',
  scopeAndConstraints: 'What is in or out of scope?',
} as const;

type Slot = keyof typeof SLOT_LABELS;

function slotOf(type: string): Slot | null {
  const value = type.replace('engagement.charter.', '') as Slot;
  return Object.prototype.hasOwnProperty.call(SLOT_LABELS, value) ? value : null;
}

export default function EngagementCharterSurface({
  state,
  busy = false,
  onEmitEdit,
}: OutcomeSurfaceProps) {
  const values = useMemo(() => {
    const result = Object.fromEntries(
      Object.keys(SLOT_LABELS).map((slot) => [slot, '']),
    ) as Record<Slot, string>;
    for (const fact of state.facts) {
      const slot = slotOf(fact.type);
      if (slot && typeof fact.content.text === 'string') result[slot] = fact.content.text;
    }
    return result;
  }, [state.facts]);
  const [draft, setDraft] = useState(values);
  useEffect(() => setDraft(values), [values]);

  return (
    <div className="charter-surface" data-testid="engagement-charter-surface">
      <header>
        <span>Brief</span>
        <h2>Engagement brief</h2>
        <p>Record what the client wants from this engagement.</p>
        <p>Enter the details yourself or use the conversation for help. Review every suggestion before adding it.</p>
      </header>
      <div className="charter-surface-fields">
        {(Object.keys(SLOT_LABELS) as Slot[]).map((slot) => (
          <label key={slot}>
            <span>{SLOT_LABELS[slot]} {slot === 'purpose' ? '*' : ''}</span>
            <textarea
              rows={slot === 'scopeAndConstraints' ? 3 : 2}
              value={draft[slot]}
              disabled={busy || state.sealed}
              onChange={(event) => setDraft({ ...draft, [slot]: event.target.value })}
              onBlur={() => {
                if (draft[slot] === values[slot]) return;
                void onEmitEdit({
                  capabilityId: 'engagement-charter.set_item',
                  input: { slot, value: draft[slot] },
                });
              }}
            />
            <small>{values[slot] ? 'Draft' : 'Not entered'}</small>
          </label>
        ))}
      </div>
      <section className="charter-proposals" aria-label="Brief suggestions">
        <div>
          <h3>Suggestions</h3>
          <span>{state.drafts.length}</span>
        </div>
        {state.drafts.length === 0 ? (
          <p>No suggestions yet. Continue the conversation or enter the brief yourself.</p>
        ) : state.drafts.map((fact) => (
          <article key={fact.factId}>
            <div>
              <span>{SLOT_LABELS[slotOf(fact.type) || 'purpose']}</span>
              <strong>{String(fact.content.text || '')}</strong>
            </div>
            <div>
              <button
                type="button"
                disabled={busy || state.sealed}
                onClick={() => void onEmitEdit({
                  capabilityId: 'engagement-charter.dismiss_proposal',
                  input: { factId: fact.factId },
                })}
              >
                Ignore
              </button>
              <button
                type="button"
                disabled={busy || state.sealed}
                onClick={() => void onEmitEdit({
                  capabilityId: 'engagement-charter.apply_proposal',
                  input: { factId: fact.factId },
                })}
              >
                Add to brief
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
